import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  getBusinessRuleSchema,
  getExtractionFields,
  getBusinessRuleStatus,
} from "~/services/businessRule.services";
import { getServiceCode } from "~/services/TGEPE/tge-agent.services";
interface IBusinessRuleStateContextProps {
  businessRules: any;
  tgeServiceCodes: any;
  isLoading: boolean;
  extractionFields: any;
  businessRuleStatus: any;
}

interface IBusinessRuleStateProviderProps {
  children: ReactNode;
  isReadOnly?: boolean;
}

const BusinessRuleStateContext = createContext<IBusinessRuleStateContextProps>({
  businessRules: [],
  tgeServiceCodes: [],
  businessRuleStatus: [],
  isLoading: true,
  extractionFields: {},
} satisfies IBusinessRuleStateContextProps);

export const BusinessRuleStateProvider = ({
  children,
}: IBusinessRuleStateProviderProps) => {
  const [businessRules, setbusinessRules] = useState<any>([]);
  const [tgeServiceCodes, setTgeServiceCodes] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [extractionFields, setExtractionFields] = useState({});
  const [businessRuleStatus, setBusinessRuleStatus] = useState<any>([]);
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      const businessRuleKey = "BusinessRule_schema";
      const tgeServiceCodes = "tgeServiceCodes";
      const businessRuleStatus = "businessRuleStatus";
      const cachedbusinessRules = sessionStorage.getItem(businessRuleKey);
      const cachedTgeServiceCodes = sessionStorage.getItem(tgeServiceCodes);
      const cachedBusinessRuleStatus =
        sessionStorage.getItem(businessRuleStatus);

      let businessRuleRes, tgeServiceCodeRes, businessRuleStatusRes;

      if (cachedbusinessRules) {
        businessRuleRes = { data: JSON.parse(cachedbusinessRules) };
      } else {
        businessRuleRes = await getBusinessRuleSchema();

        if (businessRuleRes.status && businessRuleRes.data) {
          sessionStorage.setItem(
            businessRuleKey,
            JSON.stringify(businessRuleRes.data)
          );
        }
      }

      setbusinessRules((businessRuleRes.data as any).data ?? []);

      if (cachedTgeServiceCodes) {
        tgeServiceCodeRes = { data: JSON.parse(cachedTgeServiceCodes) };
      } else {
        try {
          tgeServiceCodeRes = await getServiceCode();
          if (tgeServiceCodeRes?.status && tgeServiceCodeRes?.data) {
            sessionStorage.setItem(
              tgeServiceCodes,
              JSON.stringify(tgeServiceCodeRes.data)
            );
          } else {
            console.warn("getServiceCode returned invalid data, skipping...");
            tgeServiceCodeRes = { data: { data: [] } }; 
          }
        } catch (err) {
          console.error("getServiceCode API failed, skipping TGEPE data:", err);
          tgeServiceCodeRes = { data: { data: [] } }; 
        }
      }
      setTgeServiceCodes((tgeServiceCodeRes.data as any).data ?? []);
      if (cachedBusinessRuleStatus) {
        businessRuleStatusRes = { data: JSON.parse(cachedBusinessRuleStatus) };
      } else {
        businessRuleStatusRes = await getBusinessRuleStatus();
        if (businessRuleStatusRes.status && businessRuleStatusRes.data) {
          sessionStorage.setItem(
            businessRuleStatus,
            JSON.stringify(businessRuleStatusRes.data)
          );
        }
      }
      setBusinessRuleStatus((businessRuleStatusRes.data as any).data ?? []);
      const fields = await getExtractionFields();
      setExtractionFields(fields.data as any);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    async function initializeData() {
      await fetchData();
    }
    initializeData();
  }, [fetchData]);

  return (
    <BusinessRuleStateContext.Provider
      value={{
        businessRules,
        tgeServiceCodes,
        businessRuleStatus,
        isLoading,
        extractionFields,
      }}
    >
      {children}
    </BusinessRuleStateContext.Provider>
  );
};

export function useBusinessRule() {
  return useContext(BusinessRuleStateContext);
}
